import { useEffect, useState } from "react";
import { toast } from "sonner";
import { searchLibrarySources, type LibrarySearchResult } from "@/lib/library";
import { detectSourceIdentifier, type DetectedSourceIdentifier } from "@/lib/sourceIdentifiers";

type SearchType = "book" | "journal_article" | "chapter" | "proceedings_article";

/** Owns the My Library "find a source" search box state — the typed query,
 *  the active search type, debounced search-source-search invocation, and
 *  DOI/ISBN auto-detection that flips `searchType` for the user. */
export const useMyLibrarySearch = () => {
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState<SearchType>("book");
  const [detectedIdentifier, setDetectedIdentifier] = useState<DetectedSourceIdentifier | null>(null);
  const [detectionDismissed, setDetectionDismissed] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<LibrarySearchResult[]>([]);

  // Debounced backend search
  useEffect(() => {
    if (!searchValue.trim()) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      if (cancelled) return;
      setSearching(true);
      try {
        const data = await searchLibrarySources(searchValue, searchType);
        if (cancelled) return;
        setResults(data);
      } catch (error) {
        if (cancelled) return;
        toast.error((error as Error).message || "Search failed");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchValue, searchType]);

  // Clear stale results when source-type radio changes
  useEffect(() => {
    setResults([]);
  }, [searchType]);

  // Detect pasted DOI / ISBN and auto-flip search type
  useEffect(() => {
    const trimmed = searchValue.trim();
    if (!trimmed) {
      setDetectedIdentifier(null);
      setDetectionDismissed(false);
      return;
    }
    const detected = detectSourceIdentifier(trimmed);
    if (!detected || detected.kind === "url") {
      setDetectedIdentifier(null);
      return;
    }
    setDetectedIdentifier(detected);
    if (detectionDismissed) return;
    const isScholarly =
      searchType === "journal_article" || searchType === "chapter" || searchType === "proceedings_article";
    if (detected.kind === "doi" && !isScholarly) setSearchType("journal_article");
    else if (detected.kind === "isbn" && searchType !== "book") setSearchType("book");
  }, [searchValue, searchType, detectionDismissed]);

  const dismissDetection = () => {
    setDetectionDismissed(true);
    setDetectedIdentifier(null);
  };

  return {
    searchValue,
    setSearchValue,
    searchType,
    setSearchType,
    detectedIdentifier,
    dismissDetection,
    searching,
    results,
  };
};
